import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { SidebarIcons } from '@/assets/icons/DashboardIcons'
import { Tooltip } from '@mantine/core'
import { ERALogo } from '../../assets/icons/ERALogo'
import { edoLogo, lagosLogo, ogunLogo, bayelsaLogo } from '../../assets/images'
import { useDispatcher } from '@/providers/DispatcherProvider'

interface SidebarProps {
  isCollapsed: boolean
  onToggle: () => void
}

interface MenuItem {
  path: string
  icon: React.ComponentType<{ className?: string }>
  label: string
}

const precinctLogos = {
  'edo dispatch center': edoLogo,
  'lagos dispatch center': lagosLogo,
  'ogun dispatch center': ogunLogo,
  'bayelsa dispatch center': bayelsaLogo,
} as const

const getPrecinctLogo = (precinct?: string) => {
  if (!precinct) return edoLogo
  const key = precinct.trim().toLowerCase()
  return precinctLogos[key as keyof typeof precinctLogos] || lagosLogo
}

const precinctLogo = (precinct: string) => (
  <img src={getPrecinctLogo(precinct)} alt="Logo" width={40} height={40} />
)

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggle }) => {
  const location = useLocation()
  const { dispatcher } = useDispatcher()

  const isActive = (path: string) =>
    location.pathname === path
      ? 'bg-primary text-white rounded-lg font-semibold'
      : 'text-dark-gray-40 font-semibold hover:bg-primary-light hover:text-light-blueOne'

  const mainMenuItems: MenuItem[] = [
    { path: '/dashboard', icon: SidebarIcons.Dashboard, label: 'Dashboard' },
    {
      path: '/cases',
      icon: SidebarIcons.CasesOverview,
      label: 'Case Overview',
    },
    { path: '/map', icon: SidebarIcons.Map, label: 'Map' },
  ]

  const resourceMenuItems: MenuItem[] = [
    { path: '/responders', icon: SidebarIcons.Responders, label: 'Responders' },
    // {
    //   path: '/dispatchers',
    //   icon: SidebarIcons.Dispatchers,
    //   label: 'Dispatchers',
    // },
    {
      path: '/ambulance-providers',
      icon: SidebarIcons.AmbulanceProviders,
      label: 'Ambulance Providers',
    },
    { path: '/hospitals', icon: SidebarIcons.Hospitals, label: 'Hospitals' },
    // {
    //   path: '/registered-users',
    //   icon: SidebarIcons.RegisteredUsers,
    //   label: 'Registered Users',
    // },
  ]

  const productivityMenuItems: MenuItem[] = [
    // {
    //   path: '/leaderboard',
    //   icon: SidebarIcons.Leaderboard,
    //   label: 'Leaderboard',
    // },
    {
      path: '/shift-management',
      icon: SidebarIcons.ShiftManagement,
      label: 'Shift Management',
    },
    // { path: '/profile', icon: SidebarIcons.Profile, label: 'Account' },
    // Uncomment if needed later:
    // { path: '/notifications', icon: SidebarIcons.Notifications, label: 'Notifications' },
  ]

  const renderMenuItem = (item: MenuItem) => {
    const IconComponent = item.icon
    const linkContent = (
      <Link
        to={item.path}
        className={`p-2 flex items-center rounded-lg transition-all duration-300 ${isActive(item.path)} ${
          isCollapsed ? 'justify-center' : ''
        }`}
        aria-label={item.label}
      >
        <IconComponent
          className={`w-5 h-5 ${
            location.pathname === item.path ? 'text-white' : ''
          } ${isCollapsed ? '' : 'mr-3'}`}
        />
        {!isCollapsed && (
          <span className="font-heading text-sm">{item.label}</span>
        )}
      </Link>
    )

    if (isCollapsed) {
      return (
        <Tooltip label={item.label} position="right" withArrow key={item.path}>
          <li>{linkContent}</li>
        </Tooltip>
      )
    }

    return <li key={item.path}>{linkContent}</li>
  }

  const renderSectionHeader = (title: string) => (
    <div
      className={`text-dark-gray-40 uppercase px-3 text-xs tracking-widest font-heading font-extrabold mb-2 transition-all duration-300 ease-in-out overflow-hidden ${
        isCollapsed ? 'h-0 opacity-0 py-0' : 'opacity-100 py-1'
      }`}
    >
      {title}
    </div>
  )

  return (
    <aside
      id="main-sidebar"
      className={`fixed top-0 left-0 h-screen bg-white shadow-lg transition-all duration-300 ease-in-out z-50 ${
        isCollapsed ? 'w-16' : 'w-64'
      }`}
      role="navigation"
      aria-label="Main navigation"
      aria-expanded={!isCollapsed}
    >
      <div className="flex flex-col h-full">
        {/* Header with Client Logo and Toggle */}
        <div className="p-4 bg-white">
          <div className="flex items-center justify-between">
            <div
              className={`flex items-center transition-all duration-300 ease-in-out ${
                isCollapsed ? 'justify-center w-full' : 'space-x-2'
              }`}
            >
              {precinctLogo(
                dispatcher?.precinct_name || 'Lagos Dispatch Center'
              )}

              {/* Precinct Name */}
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100 min-w-0'
                }`}
              >
                <span className="font-heading font-semibold text-gray-800 whitespace-nowrap">
                  {dispatcher?.precinct_name || 'Dispatch Center'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Menu */}
        <div className="flex-1 overflow-y-auto">
          <nav className="mt-4 px-2">
            <div className="bg-light-blueOne rounded-lg py-2 mb-4">
              {/* Main Section */}
              {renderSectionHeader('Main')}
              <ul className="space-y-1 px-2">
                {mainMenuItems.map(renderMenuItem)}
              </ul>

              {/* Resources & Support Section */}
              {renderSectionHeader('Resources & Support')}
              <ul className="space-y-1 px-2">
                {resourceMenuItems.map(renderMenuItem)}
              </ul>
            </div>

            <div className="bg-light-blueOne rounded-lg py-2 mb-4">
              {/* Productivity Section */}
              {renderSectionHeader('Productivity')}
              <ul className="space-y-1 px-2">
                {productivityMenuItems.map(renderMenuItem)}
              </ul>
            </div>
          </nav>
        </div>

        {/* ERA Logo Footer */}
        <div className="mt-auto p-1 bg-white">
          <div className="flex justify-center">
            <ERALogo />
          </div>
        </div>
      </div>

      {/* Toggle Button */}
      <button
        onClick={onToggle}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onToggle()
          }
        }}
        className="absolute -right-3 top-12 bg-white border border-gray-200 rounded-full p-1 shadow-md hover:shadow-lg focus:outline-none transition-all duration-200 cursor-pointer hover:scale-105 hover:bg-light-blueOne"
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-expanded={!isCollapsed}
        aria-controls="main-sidebar"
        tabIndex={0}
      >
        {isCollapsed ? (
          <ChevronRight className="w-4 h-4 text-gray-600 transition-transform duration-200" />
        ) : (
          <ChevronLeft className="w-4 h-4 text-gray-600 transition-transform duration-200" />
        )}
      </button>
    </aside>
  )
}

export default Sidebar
